import { isEqual } from 'underscore';
import { cleanParams } from 'utils/ajax';

import { CONTENT_FLOWS } from './_consts';
import { getCharLimit, getCreateTrParams, getIsAFile, getSelectedFile } from './_selectors';
import { cellsToRange, containedInRanges } from './_utils';

export const ADD_EXCLUDED_RANGE = 'ADD_EXCLUDED_RANGE';
export const ADD_ALL_EXCLUDED_RANGE = 'ADD_ALL_EXCLUDED_RANGE';
export const APPROVE_BILLING_DETAILS = 'APPROVE_BILLING_DETAILS';
export const CANCEL_TABLE_SELECTION = 'CANCEL_TABLE_SELECTION';
export const ORDERING_DONE = 'ORDERING_DONE';
export const ORDERING_IN_PROGRESS = 'ORDERING_IN_PROGRESS';
export const REMOVE_EXCLUDED_RANGE = 'REMOVE_EXCLUDED_RANGE';
export const REMOVE_EXCLUDED_COLUMNS = 'REMOVE_EXCLUDED_COLUMNS';
export const SET_FILE_DATA = 'SET_FILE_DATA';
export const REMOVE_FILE_TO_UPLOAD = 'REMOVE_FILE_TO_UPLOAD';
export const RETRY_FILES_TO_UPLOAD = 'RETRY_FILES_TO_UPLOAD';
export const SET_FILE_TO_UPLOAD = 'SET_FILE_TO_UPLOAD';
export const SET_FILES_UPLOADED = 'SET_FILES_UPLOADED';
export const SET_METADATA = 'SET_METADATA';
export const SET_COLUMN_MAPPING_SOURCE_LANGUAGE = 'SET_COLUMN_MAPPING_SOURCE_LANGUAGE';
export const SET_COLUMN_MAPPING = 'SET_COLUMN_MAPPING';
export const UPDATE_COLUMN_MAPPING = 'UPDATE_COLUMN_MAPPING';
export const REMOVE_COLUMN_MAPPING = 'REMOVE_COLUMN_MAPPING';
export const SET_TARGET_COLUMN_MAPPING = 'SET_TARGET_COLUMN_MAPPING';
export const REMOVE_TARGET_COLUMN_MAPPING = 'REMOVE_TARGET_COLUMN_MAPPING';
export const UPDATE_TARGET_COLUMN_MAPPING = 'UPDATE_TARGET_COLUMN_MAPPING';
export const SET_FILTERED_CONTENT = 'SET_FILTERED_CONTENT';
export const UPDATE_CONTENT_FLOW = 'UPDATE_CONTENT_FLOW';
export const UPDATE_CSV_DELIMITER = 'UPDATE_CSV_DELIMITER';
export const UPDATE_EXCLUDED_RANGES = 'UPDATE_EXCLUDED_RANGES';
export const UPDATE_INPUT_FIELD = 'UPDATE_INPUT_FIELD';
export const UPDATE_ORDER_DETAILS = 'UPDATE_ORDER_DETAILS';
export const UPDATE_POPUP_REF_CELL = 'UPDATE_POPUP_REF_CELL';
export const UPDATE_SELECTED_CELL = 'UPDATE_SELECTED_CELL';
export const UPDATE_SELECTED_FILE = 'UPDATE_SELECTED_FILE';
export const UPDATE_SELECTED_FILE_INDEX = 'UPDATE_SELECTED_FILE_INDEX';
export const UPDATE_SELECTED_REJECTED_CELL = 'UPDATE_SELECTED_REJECTED_CELL';
export const UPDATE_SIDEBAR_FLOW = 'UPDATE_SIDEBAR_FLOW';
export const UPDATE_TABLE_SELECTED_RANGE = 'UPDATE_TABLE_SELECTED_RANGE';
export const UPDATE_UPLOADING_ERROR = 'UPDATE_UPLOADING_ERROR';
export const UPDATE_VIEW_COORDS = 'UPDATE_VIEW_COORDS';
export const UPDATE_WORD_COUNT = 'UPDATE_WORD_COUNT';
export const PROCESSING_FILE_FINISHED = 'PROCESSING_FILE_FINISHED';
export const PROCESSING_FILES_COMPLETE = 'PROCESSING_FILES_COMPLETE';
export const ASSIGN_GLOSSARY = 'ASSIGN_GLOSSARY';
export const REMOVE_GLOSSARY = 'REMOVE_GLOSSARY';
// NOTE:
// File uploading and setting requests inside UploadingFlow due to component state coupling

export const updateSidebarFlow = (sidebarFlow) => ({
  type: UPDATE_SIDEBAR_FLOW,
  sidebarFlow
});

export const updateCsvDelimiter = (csvDelimiter) => ({
  type: UPDATE_CSV_DELIMITER,
  csvDelimiter
});

export const updateUploadingError = (uploadingError) => ({
  type: UPDATE_UPLOADING_ERROR,
  uploadingError
});

export const updateInputField = ({ field, value }) => ({
  type: UPDATE_INPUT_FIELD,
  field,
  value
});

export const updateContentFlow = (contentFlow) => ({
  type: UPDATE_CONTENT_FLOW,
  contentFlow
});

export const useSelectionContentFlow = () => updateContentFlow(CONTENT_FLOWS.SELECTION);
export const useTextContentFlow = () => updateContentFlow(CONTENT_FLOWS.TEXT);
export const useUploadingContentFlow = () => updateContentFlow(CONTENT_FLOWS.UPLOADING);
export const useUploadingErrorContentFlow = () => updateContentFlow(CONTENT_FLOWS.UPLOADING_ERROR);
export const useSettingCsvSeparatorContentFlow = () =>
  updateContentFlow(CONTENT_FLOWS.SETTING_CSV_SEPARATOR);
export const useTextFileContentFlow = () => updateContentFlow(CONTENT_FLOWS.TEXT_FILE);
export const useTableContentFlow = () => updateContentFlow(CONTENT_FLOWS.TABLE);
export const useOrderDetailsFlow = () => updateContentFlow(CONTENT_FLOWS.ORDER_DETAILS);

export const setFileData = (file) => (dispatch, getState) => {
  dispatch({ type: SET_FILE_DATA, fileData: file });

  const { filesToUpload, fileData } = getState();

  if (filesToUpload.length == fileData.length) {
    if (file.hasRowsAndColumns) {
      dispatch(useTableContentFlow());
    } else {
      dispatch(useTextFileContentFlow());
    }
  }
};

export const processingFileFinished = (completedFileId) => (dispatch) => {
  dispatch({ type: PROCESSING_FILE_FINISHED, completedFileId });
};

export const setFileToUpload = (fileToUpload) => ({
  type: SET_FILE_TO_UPLOAD,
  fileToUpload
});

export const removeFileToUpload = (fileName) => (dispatch) => {
  dispatch({
    type: REMOVE_FILE_TO_UPLOAD,
    fileName
  });
};

export const retryFileToUpload = (filesToUpload) => (dispatch) => {
  dispatch({
    type: RETRY_FILES_TO_UPLOAD,
    filesToUpload
  });
};

export const setFilesUploaded = (filesUploaded) => ({
  type: SET_FILES_UPLOADED,
  filesUploaded
});

export const setMetadata = (metadata) => ({
  type: SET_METADATA,
  metadata
});

export const setColumnMapping = (columnMapping) => ({
  type: SET_COLUMN_MAPPING,
  columnMapping
});

export const updateColumnMapping = (index, columnMapping) => ({
  type: UPDATE_COLUMN_MAPPING,
  columnMapping,
  index
});

export const removeColumnMapping = (index) => ({
  type: REMOVE_COLUMN_MAPPING,
  index
});

export const updateTargetColumnMapping = (index, targetIndex, target) => ({
  type: UPDATE_TARGET_COLUMN_MAPPING,
  index,
  targetIndex,
  target
});

export const setFilteredContent = (filteredContent) => ({
  type: SET_FILTERED_CONTENT,
  filteredContent
});

export const updateViewCoords =
  ({ column, row }) =>
  (dispatch, getState) => {
    const {
      fileData: { totalRows, totalColumns }
    } = getState();

    if (column < 1) throw `Column ${column} should be bigger than 0`;
    if (row < 1) throw `Row ${row} should be bigger than 0`;
    if (column > totalColumns)
      throw `Column ${column} is outside of the bounds set by totalColumns ${totalColumns}`;
    if (row > totalRows) throw `Row ${row} is outside of the bounds set by totalRows ${totalRows}`;

    dispatch({ type: UPDATE_VIEW_COORDS, column, row });
  };

export const cancelTableSelection = () => ({
  type: CANCEL_TABLE_SELECTION
});

export const updateTableSelectedRange = (selectedRange) => ({
  type: UPDATE_TABLE_SELECTED_RANGE,
  selectedRange
});

export const updateSelectedFile = (file) => ({
  type: UPDATE_SELECTED_FILE,
  file
});

export const updateSelectedFileIndex = (index) => ({
  type: UPDATE_SELECTED_FILE_INDEX,
  index
});

export const updateSelectedCell = ({ column, row }) => ({
  type: UPDATE_SELECTED_CELL,
  column,
  row
});

export const toggleSelectTableColumn = (column) => (dispatch, getState) => {
  const state = getState();
  const { totalRows } = getSelectedFile(state);
  const {
    table: { selectedRange, viewCoords }
  } = state;
  const columnRange = {
    start: { column, row: 1 },
    end: { column, row: totalRows }
  };

  if (isEqual(columnRange, selectedRange)) return dispatch(updateTableSelectedRange({}));
  dispatch(updateTableSelectedRange(columnRange));
  dispatch(updateSelectedCell({ column, row: viewCoords.row }));
};

export const toggleSelectTableRow = (row) => (dispatch, getState) => {
  const state = getState();
  const { totalColumns } = getSelectedFile(state);
  const {
    table: { selectedRange, viewCoords }
  } = state;
  const rowRange = {
    start: { column: 1, row },
    end: { column: totalColumns, row }
  };

  if (isEqual(rowRange, selectedRange)) return dispatch(updateTableSelectedRange({}));
  dispatch(updateTableSelectedRange(rowRange));
  dispatch(updateSelectedCell({ column: viewCoords.column, row }));
};

export const selectFile = (file) => (dispatch) => {
  return dispatch(updateSelectedFile(file));
};

export const goToFile = (newIndex) => (dispatch, getState) => {
  const { fileData: files } = getState();

  dispatch(updateSelectedFileIndex(newIndex));
  dispatch(updateSelectedFile(files[newIndex]));
};

export const previousFile = () => (dispatch, getState) => {
  const { fileData: files, selectedFileIndex } = getState();

  const newIndex = selectedFileIndex > 1 ? selectedFileIndex - 1 : 0;

  dispatch(updateSelectedFileIndex(newIndex));
  dispatch(updateSelectedFile(files[newIndex]));
};

export const nextFile = () => (dispatch, getState) => {
  const { fileData: files, selectedFileIndex } = getState();

  const maxIndex = files.length - 1;
  const newIndex = selectedFileIndex < maxIndex ? selectedFileIndex + 1 : maxIndex;

  dispatch(updateSelectedFileIndex(newIndex));
  dispatch(updateSelectedFile(files[newIndex]));
};

export const selectCell = (cell) => (dispatch, getState) => {
  const { column, row } = cell;

  const {
    fileData: { totalColumns, totalRows },
    table: { selectedRange, excludedRanges }
  } = getState();

  if (column > totalColumns || row > totalRows) return;
  if (selectedRange.end) return;

  dispatch(updateSelectedCell(cell));

  if (selectedRange.start) {
    const range = cellsToRange(selectedRange.start, cell);

    return dispatch(updateTableSelectedRange(range));
  }

  if (containedInRanges(cell, excludedRanges)) return;

  dispatch(updateTableSelectedRange({ start: cell }));
};

export const getParamsCreateTr = () => async (_, getState) => {
  const state = getState();
  const isAFile = getIsAFile(state);
  const charLimit = getCharLimit(state);

  const clearedParams = await cleanParams(getCreateTrParams(state));

  const charLimitValidated = Object.fromEntries(
    Object.entries(charLimit)
      .map(([key, value]) => [key, Number(value)])
      .filter(([, value]) => value)
  );

  const paramsWithCharLimit = isAFile ? { keyValueData: { char_limit: charLimitValidated } } : {};

  const params = { ...paramsWithCharLimit, ...clearedParams, isAFile };

  return params;
};

export const addToExcludedRanges = (range) => ({
  type: ADD_EXCLUDED_RANGE,
  range
});

export const addAllToExcludedRanges = (rangeAll) => ({
  type: ADD_ALL_EXCLUDED_RANGE,
  rangeAll
});

export const removeExcludedRange = (index) => ({
  type: REMOVE_EXCLUDED_RANGE,
  index
});

export const removeExcludedColumns = (indexes) => ({
  type: REMOVE_EXCLUDED_COLUMNS,
  indexes
});

export const updateExcludedRanges = (excludedRanges) => ({
  type: UPDATE_EXCLUDED_RANGES,
  excludedRanges
});

export const updateWordCount = (wordCount) => ({
  type: UPDATE_WORD_COUNT,
  wordCount
});

export const updateOrderDetails = (orderDetails) => ({
  type: UPDATE_ORDER_DETAILS,
  orderDetails
});

export const approveBillingDetails = () => ({
  type: APPROVE_BILLING_DETAILS
});

export const assignGlossary = (glossary) => ({
  type: ASSIGN_GLOSSARY,
  glossary
});

export const removeGlossary = (glossary) => ({
  type: REMOVE_GLOSSARY,
  glossary
});
